import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { ApiError } from '@/api/client';
import { useIncidents, useRetireComponent } from '@/api/queries';
import { configPath } from '@/app/scope';
import { useToast } from '@/app/toasts';
import { Button } from '@/components/Button';
import { Dialog } from '@/components/Dialog';
import { useFocusFirstInvalid } from '@/components/focus';
import type { ComponentConfig, Scope } from '@/domain/types';
import { ApproverField, ErrorSummary } from '@/features/proposals/decision';
import { explainError, useApproverName } from '@/features/proposals/decisionState';

import { isComponentConfig } from './helpers';
import { ReasonField, StaleNotice } from './shared';

/**
 * Retire a component for good (CHG-057, TDD §8.6). Nothing here is undone later: the dialog says
 * what is left on the page for a person to deal with, and lists the page's open incidents so they
 * can check whether one still names this component. It does not try to match them for you.
 * The caller mounts it per use, so the fields start blank each time.
 */
export function RetireComponentDialog({
  scope,
  config,
  open,
  onClose,
}: {
  scope: Scope;
  config: ComponentConfig;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);
  const [message, setMessage] = useState<string | null>(null);
  // The version the operator loaded; a 409 that shows a newer one lets them load it.
  const [version, setVersion] = useState(config.version);
  const [newer, setNewer] = useState<ComponentConfig | null>(null);
  const mutation = useRetireComponent(scope, config.capability_id);
  const incidents = useIncidents(scope);
  const toast = useToast();
  const navigate = useNavigate();

  const close = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    const local: Record<string, string> = {};
    if (!reason.trim()) local.reason = 'Give a reason.';
    if (!approver.name.trim()) local.changed_by = 'Say who is making the change.';
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      return;
    }
    approver.remember();
    try {
      const result = await mutation.mutateAsync({
        version,
        changed_by: approver.name.trim(),
        reason: reason.trim(),
      });
      toast.push({
        tone: 'success',
        title: `Retired ${config.display_name}`,
        ...(result.withdrawn_proposal_id
          ? { description: 'Its waiting proposal was withdrawn.' }
          : {}),
      });
      void navigate(configPath(scope));
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const current = error.conflict?.current;
        if (isComponentConfig(current) && current.version !== version) setNewer(current);
        else setMessage('Something changed while you were retiring it. Try again.');
        return;
      }
      const explained = explainError(error);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={`Retire ${config.display_name}`}
      description="This cannot be undone. The component leaves the console and its runs are refused. Its Statuspage component, if it has one, stays on the page: remove it there by hand."
      busy={mutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            type="submit"
            form="retire-form"
            loading={mutation.isPending}
            disabled={newer !== null}
          >
            Retire component
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        id="retire-form"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        noValidate
      >
        <section aria-label="Open incidents on the page" className="flex flex-col gap-1.5">
          <p className="text-body font-medium">
            Open incidents on the page: check whether any lists this component
          </p>
          {incidents.isPending ? (
            <p className="text-body text-ink-3">Reading the page&hellip;</p>
          ) : incidents.isError ? (
            <p className="text-body text-ink-3">
              The page&rsquo;s incidents could not be read. Check the page by hand.
            </p>
          ) : incidents.data.incidents.length === 0 ? (
            <p className="text-body text-ink-3">No open incidents on the page.</p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {incidents.data.incidents.map((incident) => (
                <li key={incident.id} className="text-body">
                  <span className="font-medium">{incident.name}</span>
                  <span className="text-ink-3">
                    {' '}
                    &middot;{' '}
                    {incident.components.length > 0
                      ? incident.components.map((c) => c.name).join(', ')
                      : 'no components listed'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {newer ? (
          <StaleNotice
            current={newer}
            onLoad={() => {
              setVersion(newer.version);
              setNewer(null);
            }}
            disabled={mutation.isPending}
          />
        ) : null}

        <ReasonField
          value={reason}
          onChange={setReason}
          error={fieldErrors.reason}
          disabled={mutation.isPending}
          placeholder="e.g. A test component the simulator never posted."
        />
        <ApproverField
          label="Changed by"
          value={approver.name}
          onChange={approver.setName}
          error={fieldErrors.changed_by}
          disabled={mutation.isPending}
        />
        <ErrorSummary message={message} />
      </form>
    </Dialog>
  );
}
